import React, { Suspense, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import { closeOfficeProduct3D } from "../../../actions/officeProduct3D.actions";
import "../../../scss/adminOffice/products/_customerProduct3DPreview-AO.scss";

const CustomerProduct3DPreviewAO = React.lazy(() => import("../Products/CustomerProduct3DPreviewAO.jsx"));

const pick = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const normalizeProduct = ({ productId, title, sourceProduct, cachedProduct }) => {
    const source = sourceProduct && typeof sourceProduct === "object" ? sourceProduct : {};
    const payload = source.payload && typeof source.payload === "object" ? source.payload : {};
    const cached = cachedProduct && typeof cachedProduct === "object" ? cachedProduct : {};

    return {
        ...cached,
        ...source,
        _id: productId,
        id: productId,
        fullName: pick(
            source.displayFullNameAO,
            source.fullName,
            source.title,
            cached.fullName,
            cached.name,
            title,
            "Товар"
        ),
        name: pick(
            source.displayNameAO,
            source.name,
            cached.name,
            source.title,
            title,
            "Товар"
        ),
        article: pick(source.article, payload.article, cached.article),
        categoryName: pick(source.categoryName, payload.categoryName, cached.categoryName),
        nameMaterial: pick(source.nameMaterial, payload.materialName, cached.nameMaterial),
        productionMaterialName: pick(
            source.productionMaterialName,
            payload.materialName,
            cached.productionMaterialName
        ),
        height: pick(source.height, payload.height, cached.height),
        width: pick(source.width, payload.width, cached.width),
        length: pick(source.length, payload.length, cached.length),
        quantity: pick(source.quantity, payload.quantity, cached.quantity, 0),
        price: pick(source.effectivePrice, source.price, payload.price, cached.price),
        pricePerMeter: pick(
            source.effectivePricePerMeter,
            source.pricePerMeter,
            payload.pricePerMeter,
            cached.pricePerMeter
        ),
        effectivePrice: pick(source.effectivePrice, source.price, payload.price, cached.price),
        effectivePricePerMeter: pick(
            source.effectivePricePerMeter,
            source.pricePerMeter,
            payload.pricePerMeter,
            cached.pricePerMeter
        ),
        pricingMode: pick(source.effectivePricingMode, source.pricingMode, payload.pricingMode, cached.pricingMode),
        effectivePricingMode: pick(source.effectivePricingMode, source.pricingMode, payload.pricingMode, cached.pricingMode),
    };
};

export default function GlobalOfficeProduct3DViewer() {
    const dispatch = useDispatch();
    const viewer = useSelector((state) => state.officeProduct3D || {});
    const modelsData = useSelector((state) => state.office?.modelsData || {});

    const {
        open,
        officeId,
        productId,
        title,
        product: sourceProduct,
        openedAt,
    } = viewer;

    const cachedProduct = productId ? modelsData?.[productId]?.product : null;

    const product = useMemo(() => normalizeProduct({
        productId,
        title,
        sourceProduct,
        cachedProduct,
    }), [productId, title, sourceProduct, cachedProduct]);

    useEffect(() => {
        if (!open || typeof document === "undefined") return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event) => {
            if (event.key !== "Escape") return;

            event.preventDefault();
            event.stopPropagation();
            if (typeof event.stopImmediatePropagation === "function") {
                event.stopImmediatePropagation();
            }

            dispatch(closeOfficeProduct3D());
        };

        document.addEventListener("keydown", onKeyDown, true);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", onKeyDown, true);
        };
    }, [dispatch, open, openedAt]);

    if (!open || !officeId || !productId || typeof document === "undefined") return null;

    return createPortal(
        <Suspense
            fallback={
                <div className="global-product-3d-loading-AO">
                    <div>
                        <i />
                        <strong>Загружаем 3D-просмотр…</strong>
                    </div>
                </div>
            }
        >
            <CustomerProduct3DPreviewAO
                isOpen
                officeId={officeId}
                product={product}
                products={[]}
                categories={[]}
                standalone
                globalLayer
                onClose={() => dispatch(closeOfficeProduct3D())}
            />
        </Suspense>,
        document.body
    );
}
